/**
 * MKS-268 — WeeklyTrendBars primitive.
 *
 * Purely presentational 10-week usage sparkline sitting next to
 * `UsageGauge.tsx`: no data fetching, no hooks beyond rendering. Imports
 * `USAGE_GAUGE_THRESHOLDS` from `UsageGauge` rather than re-typing the
 * threshold numbers, so the sparkline and the gauge can never drift apart —
 * the gauge itself is left untouched, so the flag-OFF surface stays
 * byte-identical.
 *
 * A `peakPct: null` slot (an absent week — D3) renders a dashed empty track
 * with NO fill element, visually distinct from a real `peakPct: 0` slot,
 * which still renders a minimum-height fill. `role="progressbar"` is never
 * used here: `UsageTab.test.tsx` asserts exactly 2 progressbars per row
 * (the two `UsageGauge`s) and must keep passing untouched.
 */
import { format } from 'date-fns';
import type { CSSProperties } from 'react';
import { USAGE_GAUGE_THRESHOLDS } from './UsageGauge';
import type { BotUsageWeeklyTrendPoint } from '../../services/api/botUsage';

const TRACK_HEIGHT_PX = 28;
const BAR_WIDTH_PX = 6;

/** Same three threshold values `UsageGauge`'s `fillColour` uses, imported
 * rather than re-typed. */
function fillColour(pct: number): string {
  if (pct >= USAGE_GAUGE_THRESHOLDS.urgent) return '#ef4444'; // red
  if (pct >= USAGE_GAUGE_THRESHOLDS.warn) return '#f59e0b'; // amber
  return '#10b981'; // emerald
}

/**
 * Turns an ISO string into a Date carrying its UTC calendar day in local
 * terms, so `format` from `date-fns` (which reads local getters) prints the
 * UTC week regardless of the machine timezone. Falls back to the raw
 * `weekStart` string when the ISO string does not parse.
 */
function utcCalendarDateLocal(iso: string): Date | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function slotTitle(point: BotUsageWeeklyTrendPoint): string {
  const start = utcCalendarDateLocal(point.weekStart);
  if (!start) return point.weekStart;

  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const range = `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
  const value = point.peakPct === null ? 'no data' : `${Math.round(point.peakPct * 100)}%`;
  return `${range}: ${value}`;
}

export interface WeeklyTrendBarsProps {
  points: BotUsageWeeklyTrendPoint[];
  ariaLabel: string;
}

export function WeeklyTrendBars({ points, ariaLabel }: WeeklyTrendBarsProps) {
  return (
    <div role="img" aria-label={ariaLabel} className="flex items-end gap-[2px]">
      {points.map((point, index) => {
        const title = slotTitle(point);

        if (point.peakPct === null) {
          const emptyStyle: CSSProperties = {
            width: `${BAR_WIDTH_PX}px`,
            height: `${TRACK_HEIGHT_PX}px`,
          };
          return (
            <div
              key={`${point.weekStart}-${index}`}
              data-testid="weekly-trend-empty"
              title={title}
              className="border border-dashed border-gray-300 bg-transparent"
              style={emptyStyle}
            />
          );
        }

        const safePct = Math.min(1, Math.max(0, point.peakPct));
        const fillHeight = Math.max(2, Math.round(safePct * TRACK_HEIGHT_PX));
        const trackStyle: CSSProperties = {
          width: `${BAR_WIDTH_PX}px`,
          height: `${TRACK_HEIGHT_PX}px`,
        };
        const fillStyle: CSSProperties = {
          height: `${fillHeight}px`,
          background: fillColour(safePct),
        };

        return (
          <div
            key={`${point.weekStart}-${index}`}
            title={title}
            className="relative overflow-hidden rounded-sm bg-gray-100"
            style={trackStyle}
          >
            <div
              data-testid="weekly-trend-fill"
              className="absolute bottom-0 left-0 w-full"
              style={fillStyle}
            />
          </div>
        );
      })}
    </div>
  );
}
